/* survival-analysis.ts */

// TODO Check if computation of months is correct and clean up

/* Utils */
import { replaceBooleanStrings } from "src/utils/helper/data-transformation";

/* Constants */
import { Insights } from "src/shared/constants/common/Common";

/* Interfaces */
import { BaseChartProps } from "src/shared/interfaces/charts/BaseChart";
import { SurvivalAnalysisProps } from "src/shared/interfaces/SurvivalAnalysis";
import { ChartType } from "src/shared/constants/charts/Chart";

const daysToMonths = (day: number) => {
  return Math.round(day / 30.4375);
};

const convertDaysToMonthts = (
  obj: any,
  fields: string[]
) => {
  fields.forEach((field) => {
    if (obj[field]?.x) {
      obj[field].x = obj[field].x.map(daysToMonths);
    }
  });
};
/**
 * Get the types of the data to create the layout (basicLayout.tsx).
 *
 * @param {any} data
 * @returns {ChartType[]}
 */
const getTypes = (
  data: SurvivalAnalysisProps
): ChartType[] => {
  const types = (data.data ?? []).map(
    (data: { type: string }) => data.type
  );

  return types;
};

/**
 * Transform survival anaysis data.
 *
 * @param {any} data - The data to transform.
 * @returns {BaseChartProps<SurvivalAnalysisProps>} - The transformed data.
 */
export const transformSurvivalAnalysisData = (
  data: any
): BaseChartProps<SurvivalAnalysisProps, ChartType[]> => {
  const types = getTypes(data);

  const transformedData = (data.data ?? []).reduce(
    (acc: any, curr: any) => {
      curr.name =
        curr.name === null
          ? Insights.FULL_SAMPLE
          : curr.name;

      convertDaysToMonthts(curr, [
        "trace",
        "ciLower",
        "ciUpper",
        "censored",
      ]);

      if (curr.type === ChartType.TABLE) {
        curr.data.forEach((row: any) => {
          row.timeline = daysToMonths(row.timeline);
        });
      }

      if (!acc[curr.type]) {
        acc[curr.type] = [];
      }
      acc[curr.type].push(curr);
      return acc;
    },
    {}
  );

  // No-cohort completeness: the single "Full sample" curve only covers patients
  // whose vital status (death) was looked for (`total`); everyone else in the
  // cohort (`totalCohortSize`) was never assessed and is dropped from the
  // analysis. Surface that difference as a "not looked for" count on the group
  // so the legend can split it out (grey) - mirroring descriptive statistics.
  // Death is binary, so there is no "looked for, no value" state.
  const scatterGroups = transformedData[ChartType.SCATTER];
  if (
    scatterGroups?.length === 1 &&
    scatterGroups[0].name === Insights.FULL_SAMPLE &&
    data.totalCohortSize != null &&
    data.total != null
  ) {
    const notLookedForCount =
      data.totalCohortSize - data.total;
    if (notLookedForCount > 0) {
      scatterGroups[0].notLookedForCount = notLookedForCount;
    }
  }

  return {
    total: data.total,
    totalCohortSize: data.totalCohortSize,
    types: types,
    data: replaceBooleanStrings(transformedData),
  };
};
